import { TaskCreate } from "@common/contracts/task.contract";
import { MAX_PER_TRANSACTION_TASK_CREATION } from "@common/schemas/task.schema";
import { PersistableView } from "@common/types/views/views";
import { PerEntityTaskGroup } from "./taskCreation.types";

export const isTaskSplitPerTransaction = ({
    values,
    hasManuallySelectedTransactions,
}: {
    values: Pick<TaskCreate, "createTaskPerTransaction">;
    hasManuallySelectedTransactions: boolean;
}): boolean =>
    hasManuallySelectedTransactions && values.createTaskPerTransaction === true;

export const hasTaskRelations = (
    values: Pick<
        TaskCreate,
        "blockedByTaskIds" | "blockingTaskIds" | "relatedTaskIds"
    >,
): boolean =>
    [
        values.blockedByTaskIds,
        values.blockingTaskIds,
        values.relatedTaskIds,
    ].some((ids) => (ids?.length ?? 0) > 0);

export const getGroupTaskCount = ({
    group,
    isPerTransaction,
}: {
    group: PerEntityTaskGroup;
    isPerTransaction: boolean;
}): number => (isPerTransaction ? (group.transactionIds?.length ?? 1) : 1);

export const getTotalTaskCount = ({
    entityGroups,
    isPerTransaction,
    transactionCount,
}: {
    entityGroups: PerEntityTaskGroup[];
    isPerTransaction: boolean;
    transactionCount: number;
}): number => {
    if (entityGroups.length > 1) {
        return entityGroups.reduce(
            (count, group) =>
                count + getGroupTaskCount({ group, isPerTransaction }),
            0,
        );
    }
    return isPerTransaction ? transactionCount : 1;
};

export const getTransactionSplitBlocker = ({
    entityGroups,
    transactionCount,
}: {
    entityGroups: PerEntityTaskGroup[];
    transactionCount: number;
}): string | null => {
    if (transactionCount <= 1) {
        return "Select more than one transaction to split on";
    }
    const largestGroupSize =
        entityGroups.length > 1
            ? Math.max(
                  ...entityGroups.map(
                      (group) => group.transactionIds?.length ?? 0,
                  ),
              )
            : transactionCount;
    if (largestGroupSize > MAX_PER_TRANSACTION_TASK_CREATION) {
        return `Select up to ${MAX_PER_TRANSACTION_TASK_CREATION} transactions per entity to split on`;
    }
    return null;
};

export const getPerTransactionFilters = (
    filters: PersistableView["filters"],
): PersistableView["filters"] => ({
    ...(filters.entityIds ? { entityIds: filters.entityIds } : {}),
    ...(filters.transactionIds
        ? { transactionIds: filters.transactionIds }
        : {}),
});

export const buildGroupTaskValues = ({
    values,
    group,
    baseView,
    isPerTransaction,
}: {
    values: TaskCreate;
    group: PerEntityTaskGroup;
    baseView: PersistableView;
    isPerTransaction: boolean;
}): TaskCreate => ({
    ...(isPerTransaction ? withoutTaskRelations(values) : values),
    entityIds: [group.entityId],
    view: {
        ...baseView,
        filters: {
            ...(isPerTransaction
                ? getPerTransactionFilters(baseView.filters)
                : baseView.filters),
            entityIds: [group.entityId],
            ...(group.transactionIds?.length
                ? { transactionIds: group.transactionIds }
                : {}),
        },
    },
    createTaskPerTransaction: isPerTransaction ? true : undefined,
});

export const withoutTaskRelations = (values: TaskCreate): TaskCreate => ({
    ...values,
    blockedByTaskIds: undefined,
    blockingTaskIds: undefined,
    relatedTaskIds: undefined,
});
